/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injector} from '../di';
import {DebugContext} from '../view/index';
export class EventListener {
/**
 * @param {?} name
 * @param {?} callback
 */
constructor(public name: string,
public callback: Function){}; }

function EventListener_tsickle_Closure_declarations() {
/** @type {?} */
EventListener.prototype.name;
/** @type {?} */
EventListener.prototype.callback;
}

/**
 * \@experimental All debugging apis are currently experimental.
 */
export class DebugNode {
  nativeNode: any;
  listeners: EventListener[];
  parent: DebugElement|null;
/**
 * @param {?} nativeNode
 * @param {?} parent
 * @param {?} _debugContext
 */
constructor(nativeNode: any, parent: DebugNode|null,
private _debugContext: DebugContext) {
    this.nativeNode = nativeNode;
    if (parent && parent instanceof DebugElement) {
      parent.addChild(this);
    } else {
      this.parent = null;
    }
    this.listeners = [];
  }
/**
 * @return {?}
 */
get injector(): Injector { return this._debugContext.injector; }
/**
 * @return {?}
 */
get componentInstance(): any { return this._debugContext.component; }
/**
 * @return {?}
 */
get context(): any { return this._debugContext.context; }
/**
 * @return {?}
 */
get references(): {[key: string]: any} { return this._debugContext.references; }
/**
 * @return {?}
 */
get providerTokens(): any[] { return this._debugContext.providerTokens; }
/**
 * @deprecated since v4
 * @return {?}
 */
get source(): string { return 'Deprecated since v4'; }
}

function DebugNode_tsickle_Closure_declarations() {
/** @type {?} */
DebugNode.prototype.nativeNode;
/** @type {?} */
DebugNode.prototype.listeners;
/** @type {?} */
DebugNode.prototype.parent;
/** @type {?} */
DebugNode.prototype._debugContext;
}

/**
 * \@experimental All debugging apis are currently experimental.
 */
export class DebugElement extends DebugNode {
  name: string;
  properties: {[key: string]: any};
  attributes: {[key: string]: string | null};
  classes: {[key: string]: boolean};
  styles: {[key: string]: string | null};
  childNodes: DebugNode[];
  nativeElement: any;
/**
 * @param {?} nativeNode
 * @param {?} parent
 * @param {?} _debugContext
 */
constructor(nativeNode: any, parent: any, _debugContext: DebugContext) {
    super(nativeNode, parent, _debugContext);
    this.properties = {};
    this.attributes = {};
    this.classes = {};
    this.styles = {};
    this.childNodes = [];
    this.nativeElement = nativeNode;
  }
/**
 * @param {?} child
 * @return {?}
 */
addChild(child: DebugNode) {
    if (child) {
      this.childNodes.push(child);
      child.parent = this;
    }
  }
/**
 * @param {?} child
 * @return {?}
 */
removeChild(child: DebugNode) {
    const /** @type {?} */ childIndex = this.childNodes.indexOf(child);
    if (childIndex !== -1) {
      child.parent = null;
      this.childNodes.splice(childIndex, 1);
    }
  }
/**
 * @param {?} child
 * @param {?} newChildren
 * @return {?}
 */
insertChildrenAfter(child: DebugNode, newChildren: DebugNode[]) {
    const /** @type {?} */ siblingIndex = this.childNodes.indexOf(child);
    if (siblingIndex !== -1) {
      this.childNodes.splice(siblingIndex + 1, 0, ...newChildren);
      newChildren.forEach(c => {
        if (c.parent) {
          c.parent.removeChild(c);
        }
        c.parent = this;
      });
    }
  }
/**
 * @param {?} refChild
 * @param {?} newChild
 * @return {?}
 */
insertBefore(refChild: DebugNode, newChild: DebugNode): void {
    const /** @type {?} */ refIndex = this.childNodes.indexOf(refChild);
    if (refIndex === -1) {
      this.addChild(newChild);
    } else {
      if (newChild.parent) {
        newChild.parent.removeChild(newChild);
      }
      newChild.parent = this;
      this.childNodes.splice(refIndex, 0, newChild);
    }
  }
/**
 * @param {?} predicate
 * @return {?}
 */
query(predicate: Predicate<DebugElement>): DebugElement {
    const /** @type {?} */ results = this.queryAll(predicate);
    return results[0] || null;
  }
/**
 * @param {?} predicate
 * @return {?}
 */
queryAll(predicate: Predicate<DebugElement>): DebugElement[] {
    const /** @type {?} */ matches: DebugElement[] = [];
    _queryElementChildren(this, predicate, matches);
    return matches;
  }
/**
 * @param {?} predicate
 * @return {?}
 */
queryAllNodes(predicate: Predicate<DebugNode>): DebugNode[] {
    const /** @type {?} */ matches: DebugNode[] = [];
    _queryNodeChildren(this, predicate, matches);
    return matches;
  }
/**
 * @return {?}
 */
get children(): DebugElement[] {
    return /** @type {?} */(( this.childNodes.filter((node) => node instanceof DebugElement) as DebugElement[]));
  }
/**
 * @param {?} eventName
 * @param {?} eventObj
 * @return {?}
 */
triggerEventHandler(eventName: string, eventObj: any) {
    this.listeners.forEach((listener) => {
      if (listener.name == eventName) {
        listener.callback(eventObj);
      }
    });
  }
}

function DebugElement_tsickle_Closure_declarations() {
/** @type {?} */
DebugElement.prototype.name;
/** @type {?} */
DebugElement.prototype.properties;
/** @type {?} */
DebugElement.prototype.attributes;
/** @type {?} */
DebugElement.prototype.classes;
/** @type {?} */
DebugElement.prototype.styles;
/** @type {?} */
DebugElement.prototype.childNodes;
/** @type {?} */
DebugElement.prototype.nativeElement;
}

/**
 * \@experimental
 * @param {?} debugEls
 * @return {?}
 */
export function asNativeElements(debugEls: DebugElement[]): any {
  return debugEls.map((el) => el.nativeElement);
}
/**
 * @param {?} element
 * @param {?} predicate
 * @param {?} matches
 * @return {?}
 */
function _queryElementChildren(
    element: DebugElement, predicate: Predicate<DebugElement>, matches: DebugElement[]) {
  element.childNodes.forEach(node => {
    if (node instanceof DebugElement) {
      if (predicate(node)) {
        matches.push(node);
      }
      _queryElementChildren(node, predicate, matches);
    }
  });
}
/**
 * @param {?} parentNode
 * @param {?} predicate
 * @param {?} matches
 * @return {?}
 */
function _queryNodeChildren(
    parentNode: DebugNode, predicate: Predicate<DebugNode>, matches: DebugNode[]) {
  if (parentNode instanceof DebugElement) {
    parentNode.childNodes.forEach(node => {
      if (predicate(node)) {
        matches.push(node);
      }
      if (node instanceof DebugElement) {
        _queryNodeChildren(node, predicate, matches);
      }
    });
  }
}

// Need to keep the nodes in a global Map so that multiple angular apps are supported.
const /** @type {?} */ _nativeNodeToDebugNode = new Map<any, DebugNode>();
/**
 * \@experimental
 * @param {?} nativeNode
 * @return {?}
 */
export function getDebugNode(nativeNode: any): DebugNode|null {
  return _nativeNodeToDebugNode.get(nativeNode) || null;
}
/**
 * @return {?}
 */
export function getAllDebugNodes(): DebugNode[] {
  return Array.from(_nativeNodeToDebugNode.values());
}
/**
 * @param {?} node
 * @return {?}
 */
export function indexDebugNode(node: DebugNode) {
  _nativeNodeToDebugNode.set(node.nativeNode, node);
}
/**
 * @param {?} node
 * @return {?}
 */
export function removeDebugNodeFromIndex(node: DebugNode) {
  _nativeNodeToDebugNode.delete(node.nativeNode);
}

/**
 * A boolean-valued function over a value, possibly including context information
 * regarding that value's position in an array.
 *
 * @experimental All debugging apis are currently experimental.
 */
export interface Predicate<T> { (value: T): boolean; }



// WEBPACK FOOTER //
// ../../../../packages/core/src/debug/debug_node.ts